import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Truck, Plus } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { DELIVERY } from "@/constants/testIds";

const EMPTY = { source: "shopify", order_id: "", pickup_address: "", dropoff_address: "", zone_key: "", miles: 0 };

export default function AdminDispatch() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [select, setSelect] = useState({});  // orderId → driverId
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Optional zone filter (set by Phase 8 franchise deeplinks: ?zones=a,b,c)
  const zonesFilter = useMemo(() => {
    const raw = searchParams.get("zones") || "";
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }, [searchParams]);

  const reload = async () => {
    try { const { data } = await api.get("/admin/delivery/dispatch"); setData(data); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const clearFilter = () => { setSearchParams({}); };

  const filtered = useMemo(() => {
    if (!data) return null;
    if (zonesFilter.length === 0) return data;
    const set = new Set(zonesFilter);
    return {
      ...data,
      pending: data.pending.filter((o) => set.has(o.zone_key)),
      active: data.active.filter((o) => set.has(o.zone_key)),
    };
  }, [data, zonesFilter]);

  const assign = async (orderId) => {
    const driverId = select[orderId];
    if (!driverId) return;
    try { await api.post(`/admin/delivery/orders/${orderId}/assign`, { driver_id: driverId }); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  const cancel = async (orderId) => {
    if (!window.confirm("Cancel this order?")) return;
    try { await api.post(`/admin/delivery/orders/${orderId}/cancel`); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  const ingest = async () => {
    setBusy(true); setErr("");
    try { await api.post("/admin/delivery/orders", form); setForm(EMPTY); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  if (err) return <div className="text-rose-300">{err}</div>;
  if (!data) return <div className="font-bebas tracking-[0.3em] text-lifted-dim">LOADING…</div>;
  const view = filtered || data;

  return (
    <div data-testid={DELIVERY.dispatch} className="space-y-8">
      <header className="fade-up">
        <div className="eyebrow">Delivery admin</div>
        <h1 className="headline-xl">Dispatch board</h1>
        {zonesFilter.length > 0 && (
          <div className="mt-3 flex items-center gap-3" data-testid="dispatch-zone-filter-banner">
            <span className="chip chip--purple">Filtered: zones [{zonesFilter.join(", ")}]</span>
            <button onClick={clearFilter} className="btn-ghost !py-1.5 !px-3 !text-[0.65rem]" data-testid="dispatch-zone-filter-clear">Clear filter</button>
          </div>
        )}
      </header>

      {/* Ingest form (mocked Shopify/Supplies hook) */}
      <section data-testid={DELIVERY.ingestForm} className="glass-panel p-5">
        <div className="eyebrow !text-lifted-text-dim mb-3">Ingest order (mocked source webhook)</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <select className="input-lifted" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
            <option value="shopify">shopify</option><option value="lifted_supplies">lifted_supplies</option>
            <option value="partner">partner</option><option value="franchise">franchise</option>
          </select>
          <input className="input-lifted" placeholder="order_id" value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} />
          <input className="input-lifted md:col-span-2" placeholder="pickup address" value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} />
          <input className="input-lifted md:col-span-2" placeholder="dropoff address" value={form.dropoff_address} onChange={(e) => setForm({ ...form, dropoff_address: e.target.value })} />
          <input className="input-lifted" placeholder="zone (e.g. downtown)" value={form.zone_key} onChange={(e) => setForm({ ...form, zone_key: e.target.value })} />
          <input className="input-lifted" type="number" step="0.1" placeholder="miles" value={form.miles} onChange={(e) => setForm({ ...form, miles: parseFloat(e.target.value) || 0 })} />
        </div>
        <button data-testid={DELIVERY.ingestSubmit} onClick={ingest} disabled={busy} className="btn-lifted mt-4 !text-[0.7rem]">
          <Plus className="h-3 w-3" /> {busy ? "Ingesting…" : "Ingest order"}
        </button>
      </section>

      {/* Pending */}
      <section className="glass-panel p-6">
        <div className="eyebrow !text-lifted-text-dim mb-3">Pending orders ({data.pending.length})</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
              <th className="py-2">When</th><th>Source / order</th><th>Route</th><th>Zone</th><th>Miles</th><th className="text-right">Assign</th>
            </tr>
          </thead>
          <tbody>
            {data.pending.map((o) => (
              <tr key={o.id} data-testid={DELIVERY.pendingRow(o.id)} className="border-b border-white/5">
                <td className="py-2 text-xs text-lifted-dim">{o.created_at?.slice(0,16).replace("T"," ")}</td>
                <td><span className="chip">{o.source}</span><div className="text-xs text-lifted-mute mt-0.5">{o.order_id}</div></td>
                <td className="text-xs text-lifted-text">{o.pickup_address} → {o.dropoff_address}</td>
                <td className="text-lifted-text">{o.zone_key || "—"}</td>
                <td>{o.miles}</td>
                <td className="text-right">
                  <div className="inline-flex gap-2">
                    <select data-testid={DELIVERY.assignSelect(o.id)} className="bg-lifted-surface border border-white/10 rounded-md px-2 py-1 text-xs" value={select[o.id] || ""} onChange={(e) => setSelect({ ...select, [o.id]: e.target.value })}>
                      <option value="">Pick driver</option>
                      {data.drivers.map((d) => <option key={d.id} value={d.id}>{`${d.name || d.email} · ${d.availability}`}</option>)}
                    </select>
                    <button data-testid={DELIVERY.assignBtn(o.id)} onClick={() => assign(o.id)} disabled={!select[o.id]} className="btn-lifted !py-1.5 !px-3 !text-[0.65rem]">Assign</button>
                    <button onClick={() => cancel(o.id)} className="btn-ghost !py-1.5 !px-3 !text-[0.65rem] hover:!text-rose-300">Cancel</button>
                  </div>
                </td>
              </tr>
            ))}
            {data.pending.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-lifted-mute">No pending orders.</td></tr>}
          </tbody>
        </table>
      </section>

      {/* Active */}
      <section className="glass-panel p-6">
        <div className="eyebrow !text-lifted-text-dim mb-3">In-flight ({data.active.length})</div>
        <ul className="divide-y divide-white/5">
          {data.active.map((o) => (
            <li key={o.id} className="py-3 flex items-center justify-between text-sm">
              <div>
                <div className="font-bebas tracking-[0.14em] text-lifted-text">{o.driver_name}</div>
                <div className="text-xs text-lifted-dim">{o.pickup_address} → {o.dropoff_address}</div>
              </div>
              <span className="chip chip--green">{o.status.replace("_"," ")}</span>
            </li>
          ))}
          {data.active.length === 0 && <li className="py-3 text-lifted-mute text-sm">Nothing in transit.</li>}
        </ul>
      </section>
    </div>
  );
}
